import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, Clock, XCircle, Plus, Send, ArrowRight } from 'lucide-react';
import API from '../utils/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-50 text-blue-700',
  viewed: 'bg-purple-50 text-purple-700',
  completed: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-red-50 text-red-700',
  voided: 'bg-slate-100 text-slate-500',
  expired: 'bg-amber-50 text-amber-700',
};

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`stat-card-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500 font-medium">{label}</span>
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${colors[color]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/dashboard/stats')
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Overview of your signing activity</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/envelopes/create"
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="new-envelope-btn"
          >
            <Send size={15} />
            <span className="hidden sm:inline">New Envelope</span>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="Total Sent" value={stats?.total ?? 0} color="blue" />
        <StatCard icon={CheckCircle} label="Completed" value={stats?.completed ?? 0} color="emerald" />
        <StatCard icon={Clock} label="Pending" value={stats?.pending ?? 0} color="amber" />
        <StatCard icon={XCircle} label="Declined" value={stats?.declined ?? 0} color="red" />
      </div>

      {/* Recent Envelopes */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">Recent Envelopes</h2>
          <Link to="/envelopes" className="text-xs text-blue-700 hover:underline flex items-center gap-1" data-testid="view-all-envelopes-link">
            View all <ArrowRight size={12} />
          </Link>
        </div>

        {(!stats?.recent_envelopes || stats.recent_envelopes.length === 0) ? (
          <div className="text-center py-12 text-slate-400">
            <Inbox className="mx-auto mb-3 text-slate-300" size={32} />
            <p className="text-sm">No envelopes yet</p>
            <Link to="/envelopes/create" className="mt-3 inline-flex items-center gap-1.5 text-blue-700 text-sm hover:underline">
              <Plus size={14} /> Send your first document
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {stats.recent_envelopes.map(env => (
              <Link
                key={env.id}
                to={`/envelopes/${env.id}`}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors"
                data-testid={`envelope-row-${env.id}`}
              >
                <div className="w-8 h-8 bg-blue-50 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Send size={15} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{env.title}</p>
                  <p className="text-xs text-slate-400 truncate">{env.document_name} · {env.recipients?.length || 0} recipient(s)</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-sm font-medium uppercase tracking-wide flex-shrink-0 ${STATUS_STYLES[env.status] || 'bg-slate-100 text-slate-600'}`}>
                  {env.status}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/documents" className="bg-white border border-slate-200 rounded-sm p-5 hover:border-blue-300 hover:shadow-sm transition-all group" data-testid="quick-action-documents">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 group-hover:bg-blue-50 rounded-sm flex items-center justify-center transition-colors">
              <FileText size={20} className="text-slate-500 group-hover:text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Upload Document</p>
              <p className="text-xs text-slate-500">Upload a PDF or DOCX to sign</p>
            </div>
          </div>
        </Link>
        <Link to="/templates" className="bg-white border border-slate-200 rounded-sm p-5 hover:border-blue-300 hover:shadow-sm transition-all group" data-testid="quick-action-templates">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 group-hover:bg-blue-50 rounded-sm flex items-center justify-center transition-colors">
              <Plus size={20} className="text-slate-500 group-hover:text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Create Template</p>
              <p className="text-xs text-slate-500">Save a reusable document template</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

function Inbox(props) {
  return <FileText {...props} />;
}
